import {
  NOT_UAE,
  parseUaeGateError,
  resolveUaeGateSheet,
  uaeNextStepCopy,
  type UaeGateCta,
  type UaeGateRequest,
  type UaeGateSheet,
  type UaeProgramme,
} from '~/utils/uae-gate-client/uaeGate';

// UAE next-step sheet (ID-106): replaces the pricing modal for UAE agents. Programme state is
// written by <BillingUaeGateSheet> from /api/billing/me; the market fallback comes from the session.
export const useUaeGate = () => {
  const programmeState = useState<UaeProgramme>('uae:programme', () => NOT_UAE);
  const sheet = useState<UaeGateSheet | null>('uae:gateSheet', () => null);
  const authStore = useAuthStore();
  const { sendEventToBackend } = useTracking();
  const tourActive = useProductTourActive();

  const isUae = computed(() =>
    programmeState.value.loaded
      ? programmeState.value.isUae
      : authStore.user?.agentProfile?.market === 'UAE' && authStore.user?.agentProfile?.signupPath !== 'partner',
  );

  const nextStepCopy = computed(() => uaeNextStepCopy(programmeState.value));
  // ID-241: UAE Team plan owner (from /api/billing/me).
  const isUaeTeamOwner = computed(() => !!programmeState.value.teamOwner);

  const setProgramme = (programme: UaeProgramme) => {
    programmeState.value = programme;
  };

  const openGate = (request: UaeGateRequest): boolean => {
    // Nothing interrupts the product tour (same rule as the pricing popup).
    if (tourActive.value) return false;
    const resolved = resolveUaeGateSheet(request, programmeState.value);
    if (!resolved) return false;
    sheet.value = resolved;
    void sendEventToBackend('uae_gate_shown', {
      level: resolved.level,
      [request.feature ? 'feature' : 'action']: resolved.analyticsSubject,
      trigger: resolved.trigger,
    });
    return true;
  };

  // True when the error was a UAE gate response and the sheet (or nothing, for JOINED) handled it.
  const openFromError = (error: unknown): boolean => {
    const request = parseUaeGateError(error);
    if (!request) return false;
    openGate(request);
    return true;
  };

  const trackCta = (cta: UaeGateCta) => {
    void sendEventToBackend('uae_gate_cta_tapped', { level: sheet.value?.level ?? null, cta });
  };

  const close = () => {
    sheet.value = null;
  };

  return {
    programme: readonly(programmeState),
    sheet: readonly(sheet),
    isUae,
    nextStepCopy,
    setProgramme,
    isUaeTeamOwner,
    openGate,
    openFromError,
    trackCta,
    close,
  };
};
